import React, { useState, useEffect } from 'react';
import { ArrowRight, ChevronDown, ChevronUp, ChevronLeft, ChevronRight } from 'lucide-react';
import publicationsData from './PubData.json'; 

const Publications = () => {
  const containerClasses = 'mx-auto px-4 sm:px-6 lg:px-8 max-w-[1920px]';

  useEffect(() => {
    const htmlEl = document.documentElement;
    const originalFontSize = htmlEl.style.fontSize;
    htmlEl.style.fontSize = 'calc(100% + 2pt)';
    return () => {
      htmlEl.style.fontSize = originalFontSize;
    };
  }, []);

  const [searchQuery, setSearchQuery] = useState('');
  const [selectedYear, setSelectedYear] = useState('');
  const [expandedAbstracts, setExpandedAbstracts] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [pageInputValue, setPageInputValue] = useState('1');
  const publicationsPerPage = 75;

  // UPDATED: Dynamic imports for the ligand diagrams found in your Research/Images folder
  const ligandThumbnails = [
    require('./Research/Images/ProteinKinases/Picture1.jpg'),
    require('./Research/Images/ProteinKinases/Picture2.jpg'),
    require('./Research/Images/ProteinKinases/Picture3.jpg'),
    require('./Research/Images/ProteinKinases/Picture4.jpg'),
    require('./Research/Images/ProteinKinases/Picture5.jpg'),
    require('./Research/Images/ProteinKinases/Picture6.jpg'),
    require('./Research/Images/ProteinKinases/Picture7.jpg'),
    require('./Research/Images/cancer_cell.jpg'),
    require('./Research/Images/HSP70.jpg'),
    require('./Research/Images/kinasesMap.jpg'),
  ];

  // Helper to resolve thumbnails: Scraped URL > Specific Local File > Ligand Cycle
  const getPubThumbnail = (pub, index) => {
    const thumbData = pub.thumbnail;

    // 1. If it's a real URL from the publisher, use it
    if (thumbData && (thumbData.startsWith('http') || thumbData.startsWith('//'))) {
      return thumbData.startsWith('//') ? `https:${thumbData}` : thumbData;
    }

    // 2. If it's a specific local file name found by the scraper
    if (thumbData && thumbData.endsWith('.jpg')) {
      try {
        // Check both potential subfolders
        if (thumbData.startsWith('Picture')) {
          return require(`./Research/Images/ProteinKinases/${thumbData}`);
        }
        return require(`./Research/Images/${thumbData}`);
      } catch (e) {
        // Fall through to cycle if require fails
      }
    }

    // 3. Fallback: Cycle through the ligand images
    return ligandThumbnails[index % ligandThumbnails.length];
  };

  const sortedPublications = [...publicationsData].sort(
    (a, b) => parseInt(b.year, 10) - parseInt(a.year, 10)
  );

  const uniqueYears = Array.from(
    new Set(sortedPublications.map(pub => pub.year))
  ).sort((a, b) => b - a);

  const filteredPublications = sortedPublications.filter((pub) => {
    const matchesSearch = searchQuery === '' || 
      pub.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      pub.authors.toLowerCase().includes(searchQuery.toLowerCase()) ||
      pub.journal.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesYear = selectedYear === '' || pub.year.toString() === selectedYear.toString();
    return matchesSearch && matchesYear;
  });

  const totalPages = Math.ceil(filteredPublications.length / publicationsPerPage);
  const indexOfLastPublication = currentPage * publicationsPerPage;
  const indexOfFirstPublication = indexOfLastPublication - publicationsPerPage;
  const currentPublications = filteredPublications.slice(indexOfFirstPublication, indexOfLastPublication);

  const toggleAbstract = (id) => {
    setExpandedAbstracts(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const handlePageChange = (pageNumber) => {
    const page = Math.min(Math.max(1, pageNumber), totalPages);
    setCurrentPage(page);
    setPageInputValue(page.toString());
    const section = document.getElementById('publications-section');
    if (section) section.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="w-full">
      {/* Hero Section */}
      <section className="w-full bg-gradient-to-r from-blue-700 to-blue-500 text-white py-20">
        <div className={containerClasses}>
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl font-bold mb-6">Publications</h1>
            <p className="text-xl">Explore our contributions to computational biology and drug discovery.</p>
          </div>
        </div>
      </section>

      {/* Filters Section */}
      <section className="w-full py-8 bg-gray-100">
        <div className={containerClasses}>
          <div className="flex flex-col lg:flex-row gap-6 justify-center items-center">
            <input
              type="text"
              placeholder="Search by title, author, or journal"
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setCurrentPage(1);
                setPageInputValue('1');
              }}
              className="w-full lg:w-1/3 px-4 py-2 border border-gray-300 rounded-lg shadow-sm outline-none focus:ring-2 focus:ring-blue-500"
            />
            <select
              value={selectedYear}
              onChange={(e) => {
                setSelectedYear(e.target.value);
                setCurrentPage(1);
                setPageInputValue('1');
              }}
              className="w-full lg:w-1/3 px-4 py-2 border border-gray-300 rounded-lg shadow-sm outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Every Year</option>
              {uniqueYears.map((year) => (
                <option key={year} value={year}>{year}</option>
              ))}
            </select>
          </div>
        </div>
      </section>

      {/* Publications Section */}
      <section id="publications-section" className="w-full py-16 bg-gray-50">
        <div className={containerClasses}>
          <h2 className="text-3xl font-bold mb-12 text-center">Recent Publications</h2>
          <p className="text-gray-600 mb-6 text-center">
            Showing {indexOfFirstPublication + 1}-{Math.min(indexOfLastPublication, filteredPublications.length)} of {filteredPublications.length} publications
          </p>

          <div className="space-y-4">
            {currentPublications.map((pub, index) => {
              const pubId = pub.doi || pub.title;
              return (
                <div key={pubId} className="flex flex-col md:flex-row gap-6 px-6 py-5 bg-white rounded-xl shadow-sm border border-transparent hover:border-blue-200 hover:shadow-md transition-all">
                  <div className="flex-shrink-0 w-full md:w-48">
                    <img
                      src={getPubThumbnail(pub, index)}
                      alt={`Illustration for ${pub.title}`}
                      onError={({ currentTarget }) => {
                        const fallback = ligandThumbnails[index % ligandThumbnails.length];
                        // Avoid retrying if the local fallback itself fails.
                        if (currentTarget.getAttribute('src') !== fallback) {
                          currentTarget.src = fallback;
                        }
                      }}
                      className="w-full h-48 md:h-32 object-cover rounded-lg border border-gray-100"
                    />
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline gap-3 flex-wrap mb-2">
                      <span className="px-2 py-0.5 bg-blue-50 text-blue-700 text-xs font-bold rounded uppercase tracking-wider">{pub.year}</span>
                      <h3 className="text-lg md:text-xl font-bold text-gray-900 leading-tight">{pub.title}</h3>
                    </div>
                    <p className="text-sm text-gray-700 font-medium mb-1">{pub.authors}</p>
                    <p className="text-sm italic text-gray-500 mb-3">{pub.journal}</p>
                    
                    <div className="flex flex-wrap items-center gap-4">
                      <button
                        onClick={() => toggleAbstract(pubId)}
                        className="text-sm text-blue-600 hover:text-blue-800 font-semibold inline-flex items-center gap-1 transition-colors"
                      >
                        {expandedAbstracts[pubId] ? 'Hide Abstract' : 'View Abstract'}
                        {expandedAbstracts[pubId] ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                      </button>
                      <a href={pub.link} target="_blank" rel="noopener noreferrer" className="text-sm text-blue-600 hover:text-blue-800 font-semibold inline-flex items-center gap-1 transition-colors">
                        Read Paper <ArrowRight size={16} />
                      </a>
                    </div>
                    {expandedAbstracts[pubId] && (
                      <div className="mt-4 p-4 bg-gray-50 rounded-lg border-l-4 border-blue-400">
                        <p className="text-sm text-gray-700 leading-relaxed italic">{pub.abstract}</p>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Pagination Controls */}
          {totalPages > 1 && (
            <div className="mt-12 flex justify-center items-center gap-4">
              <button onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1} className={`p-2 rounded-full border transition-all ${currentPage === 1 ? 'text-gray-300 border-gray-200 cursor-not-allowed' : 'text-blue-600 border-blue-200 hover:bg-blue-50'}`}>
                <ChevronLeft size={20} />
              </button>
              <div className="flex items-center gap-2 font-medium text-gray-700">
                <input
                  type="number"
                  value={pageInputValue}
                  onChange={(e) => setPageInputValue(e.target.value)}
                  onBlur={() => handlePageChange(parseInt(pageInputValue))}
                  onKeyDown={(e) => e.key === 'Enter' && handlePageChange(parseInt(pageInputValue))}
                  className="w-16 py-1 px-2 text-center border-2 border-blue-100 rounded-lg focus:border-blue-500 outline-none"
                />
                <span>of {totalPages}</span>
              </div>
              <button onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages} className={`p-2 rounded-full border transition-all ${currentPage === totalPages ? 'text-gray-300 border-gray-200 cursor-not-allowed' : 'text-blue-600 border-blue-200 hover:bg-blue-50'}`}>
                <ChevronRight size={20} />
              </button>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default Publications;